import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Menu, Transition } from '@headlessui/react';
import { EllipsisHorizontalIcon, ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/24/outline';

import SlideUpModal from '~/components/Modal/SlideUpModal';
import Text from '~/components/Text';
import type { Admin } from '~/interfaces/admin';
import type { CommentType } from '~/interfaces/comment';
import type { Subscription } from '~/interfaces/subscriptions';

import ActionItem from './ActionItem';
import CommentDisplay from './CommentDisplay';
import DeleteAction from './DeleteAction';
import ReportAction from './ReportAction';

interface Props {
  comment: CommentType;
  postId: string;
  subscription?: Subscription;
  admin?: Admin;
  onDelete: () => void;
}

const ActionsOptionsDropdown = ({ comment, postId, subscription, onDelete, admin }: Props) => {
  const { t } = useTranslation();

  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [isReportModal, setIsReportModal] = useState(false);

  const isLoggedInAsAdmin = Boolean(admin);
  const userId = admin?.id;
  const commenterId = comment.commenter_id;
  const subscriptionId = subscription?.id;
  const didLoggedInUserComment = commenterId === subscriptionId;
  const showDeleteOption = didLoggedInUserComment || isLoggedInAsAdmin;

  const isRemovingCtas = isLoggedInAsAdmin && commenterId !== userId;

  return (
    <div>
      {showDeleteOption ? (
        <SlideUpModal
          title={
            <div className="pb-3">
              <Text as="p" size="xs" theme="content" weight="medium" className="flex items-center opacity-60">
                <TrashIcon className="mr-1 h-3.5 w-3.5" />
                {isRemovingCtas
                  ? `${t('comments.removing.removing_comment')}...`
                  : `${t('comments.deleting.deleting_comment')}...`}
              </Text>
            </div>
          }
          description={
            <CommentDisplay
              imageUrl={comment.avatar_url}
              name={comment.commenter}
              content={comment.content}
              time={comment.created_at}
              isModerator={comment.is_moderator_comment}
            />
          }
          isOpen={isDeleteModal}
          onClose={() => setIsDeleteModal(false)}
        >
          <DeleteAction
            postId={postId}
            commentId={comment.id}
            commenterId={comment.commenter_id}
            subscriptionId={subscription?.id}
            userId={userId}
            isLoggedInAsAdmin={isLoggedInAsAdmin}
            onDelete={() => {
              setIsDeleteModal(false);
              onDelete();
            }}
          />
        </SlideUpModal>
      ) : (
        <SlideUpModal
          title={
            <div className="pb-3">
              <Text as="p" size="xs" theme="content" weight="medium" className="flex items-center opacity-60">
                <ExclamationTriangleIcon className="mr-1 h-3.5 w-3.5" />
                {`${t('comments.reporting.filing_report_for')}...`}
              </Text>
            </div>
          }
          description={
            <CommentDisplay
              imageUrl={comment.avatar_url}
              name={comment.commenter}
              content={comment.content}
              time={comment.created_at}
              isModerator={comment.is_moderator_comment}
            />
          }
          isOpen={isReportModal}
          onClose={() => setIsReportModal(false)}
        >
          <ReportAction
            postId={postId}
            commentId={comment.id}
            subscriptionId={subscription?.id}
            onReport={() => {
              setIsReportModal(false);
            }}
          />
        </SlideUpModal>
      )}

      {/* DROPDOWN MENU */}
      <Menu as="div" className="relative inline-block text-left">
        <div>
          <Menu.Button className="pt-0.5">
            <Text as="span" size="xs" theme="content" weight="semibold" className="opacity-50 hover:opacity-100">
              <EllipsisHorizontalIcon className="h-4 w-4" />
            </Text>
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-10 w-32 origin-top-left divide-y divide-gray-100 rounded-md bg-wt-background shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="px-1">
              {showDeleteOption ? (
                <Menu.Item>
                  <div className="p-2">
                    <ActionItem
                      text={isRemovingCtas ? t('comments.removing.remove') : t('comments.deleting.delete')}
                      Icon={<TrashIcon className="h-3.5 w-3.5" />}
                      onClick={() => {
                        setIsDeleteModal(true);
                      }}
                    />
                  </div>
                </Menu.Item>
              ) : (
                <Menu.Item>
                  <div className="p-2">
                    <ActionItem
                      text={t('comments.reporting.report')}
                      Icon={<ExclamationTriangleIcon className="h-3.5 w-3.5" />}
                      onClick={() => {
                        setIsReportModal(true);
                      }}
                    />
                  </div>
                </Menu.Item>
              )}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
};

export default ActionsOptionsDropdown;
